import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon, Loader2Icon } from 'lucide-react';
import { integrationConfig, passwordMinLength } from '../../utils/integrationConfig';
import { validateAccountForm, type AccountFormErrors, type AccountFormValues } from '../../utils/accountValidation';
import type { PractitionerAccountInput } from '../../types/practitionerSignup';
interface AccountFormProps {
  submitting: boolean;
  /** Server-side error message to show above the submit button. */
  serverError?: string | null;
  /** Rendered beneath the server error (e.g. "sign in instead" action). */
  serverErrorAction?: React.ReactNode;
  onSubmit: (input: PractitionerAccountInput) => void;
}
const emptyValues: AccountFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  password_confirmation: '',
  terms_accepted: false
};
const inputStyles = 'mt-2 block w-full rounded-xl border bg-white px-4 py-3 text-base text-charcoal placeholder:text-charcoal/35 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600';
const linkStyles = 'font-medium text-purple-700 underline underline-offset-2 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 rounded';

/**
 * Practitioner account creation form.
 *
 * Form id: `practitioner-account-form`
 * Field name/id: first_name, last_name, email, password, password_confirmation, terms_accepted
 *
 * Passwords stay in component memory only until submit, are never logged, and
 * `password_confirmation` is not sent to the backend.
 */
export function AccountForm({
  submitting,
  serverError,
  serverErrorAction,
  onSubmit
}: AccountFormProps) {
  const [values, setValues] = useState<AccountFormValues>(emptyValues);
  const [errors, setErrors] = useState<AccountFormErrors>({});
  const [attempted, setAttempted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const update = <K extends keyof AccountFormValues,>(key: K, value: AccountFormValues[K]) => {
    const next = {
      ...values,
      [key]: value
    };
    setValues(next);
    if (attempted) setErrors(validateAccountForm(next));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAttempted(true);
    console.log('ACCOUNT FORM SUBMIT FIRED');
    const nextErrors = validateAccountForm(values);
    setErrors(nextErrors);
    console.log('FORM VALIDATION', nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      console.log('FORM BLOCKED BY VALIDATION', firstInvalid);
      document.getElementById(firstInvalid)?.focus();
      return;
    }
    console.log('CALLING onSubmit');
    onSubmit({
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email.trim().toLowerCase(),
      password: values.password,
      terms_accepted: values.terms_accepted
    });
  };
  const fieldProps = (name: keyof AccountFormErrors) => ({
    id: name,
    name,
    'aria-invalid': errors[name] ? true : undefined,
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
    className: `${inputStyles} ${errors[name] ? 'border-red-600' : 'border-charcoal/20'}`
  });
  const fieldError = (name: keyof AccountFormErrors) => errors[name] ? <p id={`${name}-error`} className="mt-1.5 text-sm text-red-700" data-id="element-324" data-mp="2vns2p9ta2c0e">
        {errors[name]}
      </p> : null;
  return <form id="practitioner-account-form" noValidate onSubmit={handleSubmit} className="space-y-5" data-id="element-325" data-mp="2rnhnr7nicb9e">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-326" data-mp="2muoimio4n0ws">
        <div data-id="element-327" data-mp="2mtdf8jylfv67">
          <label htmlFor="first_name" className="text-sm font-medium text-charcoal" data-id="element-328" data-mp="2hwvbnnf022vg">
            First name
          </label>
          <input {...fieldProps('first_name')} type="text" autoComplete="given-name" required value={values.first_name} onChange={e => update('first_name', e.target.value)} data-id="element-329" data-mp="3fd2isowxqyz1" />
          {fieldError('first_name')}
        </div>
        <div data-id="element-330" data-mp="2z5nwktc6dcz0">
          <label htmlFor="last_name" className="text-sm font-medium text-charcoal" data-id="element-331" data-mp="2z6fsk59opcpt">
            Last name
          </label>
          <input {...fieldProps('last_name')} type="text" autoComplete="family-name" required value={values.last_name} onChange={e => update('last_name', e.target.value)} data-id="element-332" data-mp="2l7g4ayq32zu6" />
          {fieldError('last_name')}
        </div>
      </div>

      <div data-id="element-333" data-mp="3447k2y6f2wth">
        <label htmlFor="email" className="text-sm font-medium text-charcoal" data-id="element-334" data-mp="344hd6umrdk3e">
          Email
        </label>
        <input {...fieldProps('email')} type="email" inputMode="email" autoComplete="email" required value={values.email} onChange={e => update('email', e.target.value)} data-id="element-335" data-mp="392917dka8m2b" />
        {fieldError('email')}
      </div>

      <div data-id="element-336" data-mp="erzfmcdnmjp8">
        <label htmlFor="password" className="text-sm font-medium text-charcoal" data-id="element-337" data-mp="erppmosm0cjj">
          Password
        </label>
        <div className="relative" data-id="element-338" data-mp="aqnzcxgo7atp">
          <input {...fieldProps('password')} type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={passwordMinLength} aria-describedby={errors.password ? 'password-error' : 'password-hint'} value={values.password} onChange={e => update('password', e.target.value)} className={`${fieldProps('password').className} pr-12`} data-id="element-339" data-mp="apcsgorxhmyd" />
          <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute right-2 top-1/2 mt-1 -translate-y-1/2 rounded-lg p-2 text-charcoal/55 hover:text-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600" data-id="element-340" data-mp="5tdxujg0b8pi">
            {showPassword ? <EyeOffIcon className="w-4 h-4" aria-hidden="true" data-id="element-341" data-mp="1o9wqc0tir3sh" /> : <EyeIcon className="w-4 h-4" aria-hidden="true" data-id="element-342" data-mp="1ociyo2o8hnbr" />}
          </button>
        </div>
        {errors.password ? fieldError('password') : <p id="password-hint" className="mt-1.5 text-sm text-charcoal/55" data-id="element-343" data-mp="1y7ax18pn4e7l">
            At least {passwordMinLength} characters. You'll use this to sign into the app.
          </p>}
      </div>

      <div data-id="element-344" data-mp="15kq6gagzo653">
        <label htmlFor="password_confirmation" className="text-sm font-medium text-charcoal" data-id="element-345" data-mp="15k80gm4iydjz">
          Confirm password
        </label>
        <input {...fieldProps('password_confirmation')} type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={values.password_confirmation} onChange={e => update('password_confirmation', e.target.value)} data-id="element-346" data-mp="1f9rmg0njanj2" />
        {fieldError('password_confirmation')}
      </div>

      <div data-id="element-347" data-mp="f9671nyxg3zc">
        <div className="flex items-start gap-3" data-id="element-348" data-mp="f7leg3w6av44">
          <input id="terms_accepted" name="terms_accepted" type="checkbox" required checked={values.terms_accepted} onChange={e => update('terms_accepted', e.target.checked)} aria-invalid={errors.terms_accepted ? true : undefined} aria-describedby={errors.terms_accepted ? 'terms_accepted-error' : undefined} className="mt-1 h-4 w-4 shrink-0 rounded border-charcoal/30 text-purple-700 accent-purple-700 focus:ring-2 focus:ring-purple-600" data-id="element-349" data-mp="abwbrrouwy3l" />
          <label htmlFor="terms_accepted" className="text-sm text-charcoal/80 leading-relaxed" data-id="element-350" data-mp="3drlb0eaghatd">
            I agree to the{' '}
            {integrationConfig.termsOfServiceUrl ? <a href={integrationConfig.termsOfServiceUrl} target="_blank" rel="noopener noreferrer" className={linkStyles} data-id="element-351" data-mp="38ul6i7t98g4d">
                Terms of Service
              </a> :
          // TODO(dev): set VITE_TERMS_OF_SERVICE_URL to link this.
          <span className="font-medium text-charcoal" data-id="element-352" data-mp="11fyzvoo62lmi">Terms of Service</span>}{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className={linkStyles} data-id="element-353" data-mp="mjrggqkl1o8a">
              Privacy Policy
            </a>
            .
          </label>
        </div>
        {fieldError('terms_accepted')}
      </div>

      {serverError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-id="element-354" data-mp="ijh6l2to6do5">
          <p data-id="element-355" data-mp="3v2wq7xu5oy3">{serverError}</p>
          {serverErrorAction && <div className="mt-2" data-id="element-356" data-mp="8scss6fqcyuw">{serverErrorAction}</div>}
        </div>}

      <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-purple-700 px-8 py-4 text-base font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white" data-id="element-357" data-mp="8q9smtkv5pcg">
        {submitting ? <>
            <Loader2Icon aria-hidden="true" className="w-4 h-4 animate-spin" data-id="element-358" data-mp="3gd40m3qzgdgy" />
            Creating your account…
          </> : 'Continue to membership'}
      </button>
    </form>;
}